interface RiskReversalSectionProps {
  onOpenForm: () => void;
}

const guarantees = [
  {
    icon: 'ri-eye-line',
    title: 'See It Before You Commit',
    desc: 'We build you a free preview of your new website first. You see exactly what you\'re getting — before you spend a single dollar.',
    tag: 'Free Preview',
    tagColor: 'emerald',
  },
  {
    icon: 'ri-checkbox-circle-line',
    title: 'You Approve Before We Launch',
    desc: 'Nothing goes live without your sign-off. You review every page, every detail. If something\'s not right, we fix it.',
    tag: 'Full Approval Before Launch',
    tagColor: 'teal',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'No Long-Term Lock-In',
    desc: '90-day initial term to build your foundation, then month-to-month. No annual contracts, no penalty to cancel after that.',
    tag: 'Month-to-Month After 90 Days',
    tagColor: 'emerald',
  },
];

export default function RiskReversalSection({ onOpenForm }: RiskReversalSectionProps) {
  return (
    <section className="bg-white py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <p className="text-emerald-500 text-xs font-bold tracking-widest uppercase mb-4">Our Commitment</p>
          <h2 className="text-3xl md:text-5xl font-black text-[#0d1117] leading-tight mb-5">
            No Risk.{' '}
            <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
              No Obligation.
            </span>
          </h2>
          <p className="text-[#6b7280] text-base md:text-lg">
            We removed every reason to hesitate. The preview is free. The approval is yours. The contract is short. All the risk is on us.
          </p>
        </div>

        {/* Guarantee cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {guarantees.map((g) => (
            <div
              key={g.title}
              className="relative bg-[#f8faf9] border border-[#e5e7eb] rounded-2xl p-8 hover:border-emerald-200 hover:shadow-md transition-all duration-300 group overflow-hidden"
            >
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-t-2xl"></div>

              <div className="w-13 h-13 w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 mb-6">
                <i className={`${g.icon} text-emerald-600`} style={{ fontSize: '24px' }}></i>
              </div>

              <span className="inline-block bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 text-xs font-bold px-3 py-1 rounded-full mb-4">
                {g.tag}
              </span>

              <h3 className="text-[#0d1117] font-bold text-lg leading-snug mb-3">{g.title}</h3>
              <p className="text-[#6b7280] text-sm leading-relaxed">{g.desc}</p>
            </div>
          ))}
        </div>

        {/* Central reassurance block */}
        <div className="bg-[#0d1117] rounded-2xl px-8 md:px-14 py-10 md:py-12 text-center max-w-3xl mx-auto">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-500/20 border border-emerald-500/30 mx-auto mb-5">
            <i className="ri-shield-check-line text-emerald-400" style={{ fontSize: '26px' }}></i>
          </div>
          <h3 className="text-white font-black text-xl md:text-2xl mb-3">
            The only thing you have to lose is a bad website.
          </h3>
          <p className="text-white/50 text-sm md:text-base leading-relaxed mb-7 max-w-xl mx-auto">
            You get a free preview of your new site built by our team — no payment, no sign-up required. If you love it, we launch it. If not, no hard feelings.
          </p>
          <button
            onClick={onOpenForm}
            className="whitespace-nowrap inline-flex items-center gap-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-base px-9 py-4 rounded-full hover:opacity-90 hover:scale-[1.02] transition-all duration-200 cursor-pointer"
          >
            <i className="ri-eye-line" style={{ fontSize: '18px' }}></i>
            Get My Free Preview
          </button>
          <p className="text-white/25 text-xs mt-4">No credit card. No contract. No pressure.</p>
        </div>

      </div>
    </section>
  );
}
